import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { eventsApi } from './api';
import { surveysApi } from '@/features/surveys/api';
import { bsEventApi } from '@/features/events/survey-config/api';
import { queryKeys } from '@/lib/api/queryKeys';
import {
  localInputToIso,
  rangeBounds,
  withinProjectRange,
} from '@/features/project/planner/dates';
import { DatePicker } from '@/components/ui/DatePicker';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import type { XkEvent } from '@/lib/api/types';

/** Default survey template auto-applied to every new event (matched by name). */
const DEFAULT_TEMPLATE_NAME = 'Xotski Default Wheel';

/**
 * Right-panel create form for an event. An event always belongs to a project
 * (xk-event-group), so the form leads with a project picker scoped to the
 * brand. Reused by the brand home "Add events" card via FormPanelHost.
 */
export function EventForm({
  brandId,
  projectId,
  onCreated,
  onCancel,
}: {
  brandId: number;
  /** Optional pre-selected project. */
  projectId?: number;
  onCreated: (event: XkEvent) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();

  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId),
    queryFn: () => eventsApi.listGroups({ companyId: brandId }),
    enabled: Number.isFinite(brandId) && brandId > 0,
  });
  const projects = groupsQ.data?.items ?? [];

  const [groupId, setGroupId] = useState<number | ''>(projectId ?? '');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [startLocal, setStartLocal] = useState('');
  const [endLocal, setEndLocal] = useState('');

  // Default the select to the first project once they load (unless preselected).
  useEffect(() => {
    if (groupId === '' && projects.length > 0) setGroupId(projectId ?? projects[0].id);
  }, [projects, groupId, projectId]);

  // The chosen project's date range clamps the event's start/end. Empty range
  // (project has no dates) means no clamp — the backend stays authoritative.
  const selectedProject = projects.find((p) => p.id === Number(groupId));
  const bounds = rangeBounds(selectedProject?.startDate, selectedProject?.endDate);

  const create = useMutation({
    mutationFn: async () => {
      const created = await eventsApi.createEvent({
        name: name.trim(),
        // Send '' (not undefined) so the create succeeds even where the
        // backend `address` column is still NOT NULL without a default.
        address: address.trim(),
        startDate: localInputToIso(startLocal),
        endDate: localInputToIso(endLocal),
        eventGroupId: Number(groupId),
        companyId: brandId,
        sectionCount: 1,
        eventStatus: 'Active',
      });
      // Auto-apply the default survey template. Best-effort: a missing template
      // or a propagation hiccup must NOT fail the event creation itself —
      // the survey can still be picked later from the event's Survey tab.
      try {
        const templates = await surveysApi.listTemplates('xotski');
        const def = templates.items.find(
          (tpl) => (tpl.name ?? '').trim().toLowerCase() === DEFAULT_TEMPLATE_NAME.toLowerCase(),
        );
        if (def) {
          await bsEventApi.create({ eventId: created.id, sourceTemplateId: def.id });
        }
      } catch {
        // swallow — event is created regardless of template application
      }
      return created;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.byGroup(Number(groupId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.events.byBrand(brandId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.bsEvent.byEvent(created.id) });
      toast({ tone: 'success', title: `Event "${created.name}" created` });
      onCreated(created);
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: 'Could not create event', description: e.message }),
  });

  const canSubmit =
    name.trim().length >= 2 && groupId !== '' && !create.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const startIso = localInputToIso(startLocal);
    const endIso = localInputToIso(endLocal);
    if (startIso && endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      toast({ tone: 'error', title: 'End date must be after the start date' });
      return;
    }
    if (!withinProjectRange(startIso, endIso, selectedProject?.startDate, selectedProject?.endDate)) {
      toast({ tone: 'error', title: t('eventForm.outOfRange') });
      return;
    }
    create.mutate();
  };

  // No project yet — events need one. Nudge the user to create a project first.
  if (!groupsQ.isLoading && projects.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-[var(--color-xk-text-muted)]">
          {t('eventForm.noProjects')}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
        >
          {t('stock.dialog.cancel')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <label className="block">
        <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          {t('eventForm.project')} <span className="text-[var(--color-accent)]">*</span>
        </div>
        <select
          value={groupId}
          onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : '')}
          className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] focus:border-[var(--color-xk-accent)] focus:outline-none"
        >
          {groupsQ.isLoading && <option value="">{t('common.loading')}</option>}
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          {t('eventForm.name')} <span className="text-[var(--color-accent)]">*</span>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('eventForm.namePlaceholder')}
          autoFocus
          required
          minLength={2}
          className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-xk-accent)] focus:outline-none"
        />
      </label>

      <label className="block">
        <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          {t('eventForm.address')}
        </div>
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={t('eventForm.addressPlaceholder')}
          className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-xk-accent)] focus:outline-none"
        />
      </label>

      <div className="grid grid-cols-1 gap-3">
        <label className="block">
          <div className="mb-1.5 flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            <CalendarClock className="h-3 w-3" />
            {t('eventForm.starts')}
          </div>
          <DatePicker
            mode="datetime"
            value={startLocal}
            onChange={setStartLocal}
            min={bounds.min}
            max={endLocal || bounds.max}
          />
        </label>
        <label className="block">
          <div className="mb-1.5 flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            <CalendarClock className="h-3 w-3" />
            {t('eventForm.ends')}
          </div>
          <DatePicker
            mode="datetime"
            value={endLocal}
            onChange={setEndLocal}
            min={startLocal || bounds.min}
            max={bounds.max}
          />
        </label>
      </div>

      <div className="mt-auto flex items-center justify-end gap-2 border-t border-[var(--color-xk-border)] pt-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
        >
          {t('stock.dialog.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2 text-xs font-extrabold text-[#0A0A0A] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {create.isPending ? t('stock.dialog.saving') : t('eventForm.create')}
        </button>
      </div>
    </form>
  );
}
